import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { getAnalytics, calculateDurationMinutes } from '@/lib/analytics';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, displayName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (!error && data.user) {
        // Get user role from user_account table
        const { data: userData } = await supabase
          .from('user_account')
          .select('role')
          .eq('external_auth_user', data.user.id)
          .single();

        // Identify user in analytics
        const analytics = getAnalytics();
        analytics?.identify(data.user.id, {
          email: data.user.email,
          role: userData?.role || 'unknown'
        });

        // Track login event
        analytics?.track({
          name: 'user_logged_in',
          properties: {
            role: userData?.role || 'unknown',
            method: 'password'
          }
        });

        // Store login time for session duration tracking
        sessionStorage.setItem('login_time', new Date().toISOString());
      }

      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            display_name: displayName,
          },
        },
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    try {
      // Calculate session duration before logout
      const loginTime = sessionStorage.getItem('login_time');
      if (loginTime) {
        const sessionDuration = calculateDurationMinutes(loginTime);

        // Track logout event
        const analytics = getAnalytics();
        analytics?.track({
          name: 'user_logged_out',
          properties: {
            session_duration_minutes: sessionDuration
          }
        });

        // Reset analytics identity
        analytics?.reset();
      }

      // Only attempt API signout if we have a session
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        // Session exists, attempt proper signout
        await supabase.auth.signOut({ scope: 'global' });
      }
    } catch (error) {
      // Ignore all errors - we're logging out regardless
      console.warn('Signout API call failed, forcing local cleanup:', error);
    } finally {
      // ALWAYS force complete cleanup, even if API call failed
      setSession(null);
      setUser(null);

      // Clear ALL Supabase auth-related storage keys
      const keysToRemove = Object.keys(localStorage).filter(key =>
        key.startsWith('sb-') || key.includes('supabase')
      );
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Clear session storage completely
      sessionStorage.clear();

      // Force redirect to auth page (hard refresh to reset client state)
      window.location.href = '/auth';
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      
      if (error) {
        // Handle specific error cases
        if (error.message.includes('rate limit')) {
          return { error: new Error('Too many requests. Please wait a few minutes before trying again.') };
        }
        return { error };
      }
      
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const value = {
    user,
    session,
    isLoading,
    isAuthenticated: !!user,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
